import { LocationAcuityCode, LocationTypeCode } from '@quromedical/fhir-common';
import { z } from 'zod';
import {
  BaseUUIDSearchSchema,
  DisplayReferenceSchema,
  UUIDSchema,
} from '../domain/models';
import { PaginationSchema } from '../domain/pagination';
import { type PaginatedBundle, PaginatedBundleSchema } from '../domain/schema';

const locationVirtualHospitalRefinement = (
  obj: {
    type: LocationTypeCode;
    acuity?: LocationAcuityCode;
    capacity?: number;
    parent?: string;
  },
  ctx: z.RefinementCtx,
) => {
  if (obj.type === LocationTypeCode.VirtualHospital && obj.parent) {
    ctx.addIssue({
      code: 'custom',
      message: 'Virtual Hospital must not have a parent',
      path: ['parent'],
    });
    return;
  }

  if (obj.type === LocationTypeCode.VirtualHospital && obj.acuity) {
    ctx.addIssue({
      code: 'custom',
      message: 'Virtual Hospital must not have an acuity',
      path: ['acuity'],
    });
  }

  if (obj.type === LocationTypeCode.VirtualHospital && obj.capacity) {
    ctx.addIssue({
      code: 'custom',
      message: 'Virtual Hospital must not have a count',
      path: ['capacity'],
    });
  }

  const parsed = BaseLocationSchema.safeParse(obj);

  if (!parsed.success) {
    ctx.addIssue({
      code: 'custom',
      message: parsed.error.message,
    });
  }
};

export const SupportedLocationStatus = [
  'active',
  'suspended',
  'inactive',
] as const;

export const LocationStatusSchema = z.enum(SupportedLocationStatus);

export const LocationTypeSchema = z.nativeEnum(LocationTypeCode);

export const LocationAcuitySchema = z.nativeEnum(LocationAcuityCode);

export const CapacitySchema = z.coerce.number().int().gte(0);

export const GetAcuityResponseSchema = z.record(
  z.nativeEnum(LocationAcuityCode),
  z.number(),
);

export const LocationSearchSchema = PaginationSchema.extend({
  name: z.string().optional(),
  parent: BaseUUIDSearchSchema.optional(),
  status: LocationStatusSchema.optional(),
  type: LocationTypeSchema.optional(),
});

export const BaseLocationSchema = z.object({
  status: LocationStatusSchema,
  name: z.string(),
  description: z.string(),
  type: LocationTypeSchema,
  acuity: LocationAcuitySchema.optional(),
});

export const CreateLocationSchema = BaseLocationSchema.extend({
  managingOrganization: UUIDSchema,
  parent: UUIDSchema.optional(),
  capacity: CapacitySchema.optional(),
}).superRefine(locationVirtualHospitalRefinement);

export const LocationSchema = BaseLocationSchema.extend({
  id: UUIDSchema,
  managingOrganization: DisplayReferenceSchema,
  parent: DisplayReferenceSchema.optional(),
  capacity: CapacitySchema,
});

export const UpdateLocationSchema = BaseLocationSchema.extend({
  id: UUIDSchema,
  managingOrganization: UUIDSchema.optional(),
  parent: UUIDSchema.optional(),
  capacity: CapacitySchema.optional(),
}).superRefine(locationVirtualHospitalRefinement);

export const GetLocationResponseSchema = LocationSchema;
export type GetLocationResponse = z.infer<typeof GetLocationResponseSchema>;

export const LocationGetAllResponseSchema = PaginatedBundleSchema.extend({
  locations: GetLocationResponseSchema.array(),
});
export interface LocationGetAllResponse extends PaginatedBundle {
  locations: GetLocationResponse[];
}

export type LocationSearchParams = z.infer<typeof LocationSearchSchema>;

export type Location = z.infer<typeof LocationSchema>;
export type CreateLocation = z.infer<typeof CreateLocationSchema>;

export type LocationBase = z.infer<typeof BaseLocationSchema>;

export type UpdateLocation = z.infer<typeof UpdateLocationSchema>;

export type LocationStatus = z.infer<typeof LocationStatusSchema>;
export type GetAcuityResponse = z.infer<typeof GetAcuityResponseSchema>;
